import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, DollarSign, Calendar, TrendingDown, Percent, AlertCircle, Target, Brain, ChevronDown, ChevronUp, Edit, Loader2 } from "lucide-react";

export default function CreditCards() {
  const { api } = useAuth();
  const [cards, setCards] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [strategyResult, setStrategyResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    issuer: "",
    last_four: "",
    credit_limit: "",
    current_balance: "",
    apr: "",
    minimum_payment: "",
    due_day: "1",
    statement_day: "",
    rewards_type: "",
    notes: ""
  });

  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_type: "custom",
    notes: ""
  });

  const [strategyData, setStrategyData] = useState({
    strategy: "avalanche",
    extra_monthly_payment: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, dashboardRes] = await Promise.all([
        api.get("/credit-cards"),
        api.get("/credit-cards/summary/dashboard")
      ]);
      setCards(cardsRes.data);
      setDashboard(dashboardRes.data);
    } catch (e) {
      console.error("Failed to fetch credit cards:", e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddCard = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/credit-cards", {
        ...formData,
        credit_limit: parseFloat(formData.credit_limit),
        current_balance: parseFloat(formData.current_balance),
        apr: parseFloat(formData.apr),
        minimum_payment: parseFloat(formData.minimum_payment),
        due_day: parseInt(formData.due_day),
        statement_day: formData.statement_day ? parseInt(formData.statement_day) : null
      });
      toast.success("Credit card added!");
      setShowAddModal(false);
      setFormData({
        name: "", issuer: "", last_four: "", credit_limit: "", current_balance: "",
        apr: "", minimum_payment: "", due_day: "1", statement_day: "", rewards_type: "", notes: ""
      });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add card");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setFormData({
      name: card.name || "",
      issuer: card.issuer || "",
      last_four: card.last_four || "",
      credit_limit: card.credit_limit?.toString() || "",
      current_balance: card.current_balance?.toString() || "",
      apr: card.apr?.toString() || "",
      minimum_payment: card.minimum_payment?.toString() || "",
      due_day: card.due_day?.toString() || "1",
      statement_day: card.statement_day?.toString() || "",
      rewards_type: card.rewards_type || "",
      notes: card.notes || ""
    });
    setShowEditModal(true);
  };

  const handleUpdateCard = async (e) => {
    e.preventDefault();
    if (submitting || !editingCard) return;
    setSubmitting(true);
    try {
      await api.put(`/credit-cards/${editingCard.id}`, {
        ...formData,
        credit_limit: parseFloat(formData.credit_limit),
        current_balance: parseFloat(formData.current_balance),
        apr: parseFloat(formData.apr),
        minimum_payment: parseFloat(formData.minimum_payment),
        due_day: parseInt(formData.due_day),
        statement_day: formData.statement_day ? parseInt(formData.statement_day) : null
      });
      toast.success("Credit card updated!");
      setShowEditModal(false);
      setEditingCard(null);
      setFormData({
        name: "", issuer: "", last_four: "", credit_limit: "", current_balance: "",
        apr: "", minimum_payment: "", due_day: "1", statement_day: "", rewards_type: "", notes: ""
      });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update card");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm("Delete this credit card? This will also remove payment history.")) return;
    try {
      await api.delete(`/credit-cards/${cardId}`);
      toast.success("Card deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete card");
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedCard) return;
    try {
      await api.post(`/credit-cards/${selectedCard.id}/payments`, {
        card_id: selectedCard.id,
        amount: parseFloat(paymentData.amount),
        payment_date: paymentData.payment_date,
        payment_type: paymentData.payment_type,
        notes: paymentData.notes
      });
      toast.success("Payment recorded!");
      setShowPaymentModal(false);
      setPaymentData({ amount: "", payment_date: new Date().toISOString().split("T")[0], payment_type: "custom", notes: "" });
      setSelectedCard(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    }
  };

  const fetchPaymentHistory = async (cardId) => {
    try {
      const res = await api.get(`/credit-cards/${cardId}/payments`);
      setPaymentHistory(prev => ({ ...prev, [cardId]: res.data.payments }));
    } catch (e) {
      console.error("Failed to fetch payment history:", e);
    }
  };

  const toggleCardExpand = (cardId) => {
    if (expandedCard === cardId) {
      setExpandedCard(null);
    } else {
      setExpandedCard(cardId);
      if (!paymentHistory[cardId]) {
        fetchPaymentHistory(cardId);
      }
    }
  };

  const handleAnalyzeStrategy = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post("/credit-cards/ai/payoff-strategy", {
        strategy: strategyData.strategy,
        extra_monthly_payment: parseFloat(strategyData.extra_monthly_payment) || 0
      });
      setStrategyResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const getUtilizationColor = (pct) => {
    if (pct <= 30) return "text-green-400";
    if (pct <= 50) return "text-yellow-400";
    if (pct <= 75) return "text-orange-400";
    return "text-red-400";
  };

  const getUtilizationBg = (pct) => {
    if (pct <= 30) return "bg-green-500";
    if (pct <= 50) return "bg-yellow-500";
    if (pct <= 75) return "bg-orange-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const handleSendReminders = async () => {
    try {
      const res = await api.post("/credit-cards/send-reminders");
      if (res.data.reminders_sent > 0) {
        toast.success(`Sent ${res.data.reminders_sent} payment reminder(s)!`);
      } else {
        toast.info(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminders");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <CreditCard className="w-7 h-7 text-primary" />
              Credit Cards
            </h1>
            <p className="text-gray-400 text-sm mt-1">Track and manage your credit card debt</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSendReminders}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="send-reminders-btn"
              title="Send email reminders for upcoming payments"
            >
              <AlertCircle className="w-4 h-4" />
              Send Reminders
            </button>
            <button
              onClick={() => setShowStrategyModal(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="payoff-strategy-btn"
            >
              <Brain className="w-4 h-4" />
              AI Payoff Strategy
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="add-card-btn"
            >
              <Plus className="w-4 h-4" />
              Add Card
            </button>
          </div>
        </div>

        {/* Dashboard Summary */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-xs mb-1">Total Debt</div>
              <div className="text-xl font-bold text-red-400">${dashboard.total_debt.toLocaleString()}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-xs mb-1">Credit Limit</div>
              <div className="text-xl font-bold text-white">${dashboard.total_credit_limit.toLocaleString()}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-xs mb-1">Available Credit</div>
              <div className="text-xl font-bold text-green-400">${dashboard.total_available_credit.toLocaleString()}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-xs mb-1">Utilization</div>
              <div className={`text-xl font-bold ${getUtilizationColor(dashboard.average_utilization_pct)}`}>
                {dashboard.average_utilization_pct.toFixed(1)}%
              </div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-xs mb-1">Avg APR</div>
              <div className="text-xl font-bold text-orange-400">{dashboard.weighted_average_apr.toFixed(2)}%</div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-xs mb-1">Min Payments</div>
              <div className="text-xl font-bold text-white">${dashboard.total_minimum_payments.toLocaleString()}</div>
            </div>
          </div>
        )}

        {/* Upcoming Payments Alert */}
        {dashboard?.upcoming_payments?.length > 0 && (
          <div className="bg-[#0A0A0A] border border-yellow-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              <span className="font-medium text-yellow-400">Upcoming Payments</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {dashboard.upcoming_payments.slice(0, 3).map((payment) => (
                <div key={payment.card_id} className="bg-black/30 rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium text-white">{payment.card_name}</div>
                    <div className="text-sm text-gray-400">
                      Due: {payment.due_date} ({payment.days_until} days)
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">${payment.minimum_payment.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">minimum</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Credit Cards Grid */}
        {cards.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No Credit Cards</h3>
            <p className="text-gray-400 mb-4">Add your credit cards to track balances and payments</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-2 bg-primary hover:bg-primary/80 rounded-lg"
            >
              Add Your First Card
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {cards.map((card) => (
              <div
                key={card.id}
                className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden"
                data-testid={`credit-card-${card.id}`}
              >
                {/* Card Header */}
                <div className="p-4 border-b border-white/5">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-white text-lg">{card.name}</h3>
                      <p className="text-gray-400 text-sm">{card.issuer} {card.last_four && `•••• ${card.last_four}`}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedCard(card);
                          setPaymentData(prev => ({ ...prev, amount: card.minimum_payment.toString() }));
                          setShowPaymentModal(true);
                        }}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm flex items-center gap-1"
                        data-testid={`pay-btn-${card.id}`}
                      >
                        <DollarSign className="w-3 h-3" />
                        Pay
                      </button>
                      <button
                        onClick={() => handleEditCard(card)}
                        className="p-1 text-gray-400 hover:text-primary transition-colors"
                        data-testid={`edit-card-${card.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Card Stats */}
                <div className="p-4 space-y-4">
                  {/* Balance & Limit */}
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-2xl font-bold text-white">${card.current_balance.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">of ${card.credit_limit.toLocaleString()} limit</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${getUtilizationColor(card.utilization_pct)}`}>
                        {card.utilization_pct.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">utilization</div>
                    </div>
                  </div>

                  {/* Utilization Bar */}
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getUtilizationBg(card.utilization_pct)} transition-all`}
                      style={{ width: `${Math.min(card.utilization_pct, 100)}%` }}
                    />
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-3 gap-4 pt-2">
                    <div>
                      <div className="text-xs text-gray-500">APR</div>
                      <div className="font-medium text-orange-400">{card.apr}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Min Payment</div>
                      <div className="font-medium text-white">${card.minimum_payment}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Due Day</div>
                      <div className="font-medium text-white">{card.due_day}th</div>
                    </div>
                  </div>

                  {/* Available Credit */}
                  <div className="flex justify-between items-center pt-2 border-t border-white/5">
                    <span className="text-gray-400 text-sm">Available Credit</span>
                    <span className="font-bold text-green-400">${card.available_credit.toLocaleString()}</span>
                  </div>

                  {/* Expand for History */}
                  <button
                    onClick={() => toggleCardExpand(card.id)}
                    className="w-full flex items-center justify-center gap-1 text-gray-400 hover:text-white text-sm py-2"
                  >
                    {expandedCard === card.id ? (
                      <>Hide History <ChevronUp className="w-4 h-4" /></>
                    ) : (
                      <>View History <ChevronDown className="w-4 h-4" /></>
                    )}
                  </button>

                  {/* Payment History */}
                  {expandedCard === card.id && (
                    <div className="border-t border-white/5 pt-3 space-y-2">
                      <h4 className="text-sm font-medium text-gray-400">Payment History</h4>
                      {paymentHistory[card.id]?.length > 0 ? (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {paymentHistory[card.id].map((payment) => (
                            <div key={payment.id} className="flex justify-between items-center bg-black/30 rounded p-2 text-sm">
                              <div>
                                <div className="text-white">${payment.amount.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">{payment.payment_date}</div>
                              </div>
                              <div className="text-xs text-gray-400 capitalize">{payment.payment_type}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No payments recorded yet</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Card Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-white/10">
                <h2 className="text-lg font-bold text-white">Add Credit Card</h2>
              </div>
              <form onSubmit={handleAddCard} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Card Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="Chase Sapphire"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Issuer *</label>
                    <input
                      type="text"
                      value={formData.issuer}
                      onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="Chase"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Credit Limit *</label>
                    <input
                      type="number"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="10000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Current Balance *</label>
                    <input
                      type="number"
                      value={formData.current_balance}
                      onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="2500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">APR % *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.apr}
                      onChange={(e) => setFormData({ ...formData, apr: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="19.99"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Min Payment *</label>
                    <input
                      type="number"
                      value={formData.minimum_payment}
                      onChange={(e) => setFormData({ ...formData, minimum_payment: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="35"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Due Day *</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.due_day}
                      onChange={(e) => setFormData({ ...formData, due_day: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Last 4 Digits</label>
                    <input
                      type="text"
                      maxLength="4"
                      value={formData.last_four}
                      onChange={(e) => setFormData({ ...formData, last_four: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="1234"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Rewards Type</label>
                    <select
                      value={formData.rewards_type}
                      onChange={(e) => setFormData({ ...formData, rewards_type: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    >
                      <option value="">None</option>
                      <option value="cashback">Cash Back</option>
                      <option value="points">Points</option>
                      <option value="miles">Miles</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    rows="2"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-white/10 rounded-lg hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-primary rounded-lg hover:bg-primary/80 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Add Card
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Card Modal */}
        {showEditModal && editingCard && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-white/10">
                <h2 className="text-lg font-bold text-white">Edit Credit Card</h2>
              </div>
              <form onSubmit={handleUpdateCard} className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Card Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Issuer *</label>
                    <input
                      type="text"
                      value={formData.issuer}
                      onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Credit Limit *</label>
                    <input
                      type="number"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Current Balance *</label>
                    <input
                      type="number"
                      value={formData.current_balance}
                      onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">APR % *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.apr}
                      onChange={(e) => setFormData({ ...formData, apr: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Min Payment *</label>
                    <input
                      type="number"
                      value={formData.minimum_payment}
                      onChange={(e) => setFormData({ ...formData, minimum_payment: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Due Day *</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.due_day}
                      onChange={(e) => setFormData({ ...formData, due_day: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Last 4 Digits</label>
                    <input
                      type="text"
                      maxLength="4"
                      value={formData.last_four}
                      onChange={(e) => setFormData({ ...formData, last_four: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Rewards Type</label>
                    <select
                      value={formData.rewards_type}
                      onChange={(e) => setFormData({ ...formData, rewards_type: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    >
                      <option value="">None</option>
                      <option value="cashback">Cash Back</option>
                      <option value="points">Points</option>
                      <option value="miles">Miles</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    rows="2"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); setEditingCard(null); }}
                    className="flex-1 px-4 py-2 border border-white/10 rounded-lg hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-primary rounded-lg hover:bg-primary/80 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Update Card
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedCard && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl w-full max-w-md">
              <div className="p-4 border-b border-white/10">
                <h2 className="text-lg font-bold text-white">Record Payment</h2>
                <p className="text-sm text-gray-400">{selectedCard.name}</p>
              </div>
              <form onSubmit={handleRecordPayment} className="p-4 space-y-4">
                <div className="bg-black/30 rounded-lg p-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Current Balance</span>
                    <span className="text-white font-medium">${selectedCard.current_balance.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-400">Minimum Payment</span>
                    <span className="text-white font-medium">${selectedCard.minimum_payment}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Payment Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    required
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setPaymentData({ ...paymentData, amount: selectedCard.minimum_payment.toString(), payment_type: "minimum" })}
                      className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded"
                    >
                      Minimum
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentData({ ...paymentData, amount: selectedCard.current_balance.toString(), payment_type: "full" })}
                      className="text-xs px-2 py-1 bg-green-700 hover:bg-green-600 rounded"
                    >
                      Full Balance
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Payment Date *</label>
                  <input
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedCard(null);
                    }}
                    className="flex-1 px-4 py-2 border border-white/10 rounded-lg hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    Record Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* AI Strategy Modal */}
        {showStrategyModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-400" />
                  AI Debt Payoff Strategy
                </h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Strategy</label>
                    <select
                      value={strategyData.strategy}
                      onChange={(e) => setStrategyData({ ...strategyData, strategy: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                    >
                      <option value="avalanche">Avalanche (Highest APR First)</option>
                      <option value="snowball">Snowball (Lowest Balance First)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {strategyData.strategy === "avalanche" 
                        ? "Saves most money on interest" 
                        : "Quick wins for motivation"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Extra Monthly Payment</label>
                    <input
                      type="number"
                      value={strategyData.extra_monthly_payment}
                      onChange={(e) => setStrategyData({ ...strategyData, extra_monthly_payment: e.target.value })}
                      className="w-full bg-black/50 border border-white/10 rounded px-3 py-2"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Beyond minimum payments</p>
                  </div>
                </div>

                <button
                  onClick={handleAnalyzeStrategy}
                  disabled={analyzing || cards.length === 0}
                  className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {analyzing ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyzing...</>
                  ) : (
                    <><Target className="w-4 h-4" /> Analyze Payoff Strategy</>
                  )}
                </button>

                {strategyResult && (
                  <div className="mt-4 space-y-4">
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                      <h3 className="font-medium text-purple-400 mb-2">Strategy: {strategyResult.strategy.toUpperCase()}</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-400">Total Debt</div>
                          <div className="text-white font-bold">${strategyResult.total_debt.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Min Payments</div>
                          <div className="text-white font-bold">${strategyResult.total_minimum.toLocaleString()}/mo</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Extra Payment</div>
                          <div className="text-green-400 font-bold">${strategyResult.extra_payment.toLocaleString()}/mo</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4">
                      <h4 className="font-medium text-white mb-2">AI Recommendations</h4>
                      <div className="text-sm text-gray-300 whitespace-pre-wrap">
                        {strategyResult.analysis?.raw_response || JSON.stringify(strategyResult.analysis, null, 2)}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => {
                      setShowStrategyModal(false);
                      setStrategyResult(null);
                    }}
                    className="px-4 py-2 border border-white/10 rounded-lg hover:bg-white/5"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
